import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { translations } from './i18n';
import { auth, users as usersApi } from './api';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ClientDatabase from './components/ClientDatabase';
import Downloads from './components/Downloads';
import { UserManagement, Settings } from './components/UserManagement';

i18next.use(initReactI18next).init({
  resources: translations,
  lng: localStorage.getItem('lang') || 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOffice, setActiveOffice] = useState('ahmedabad');
  const { i18n } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      auth.me().then(res => {
        setUser(res.data);
        if (res.data.office_access !== 'both') setActiveOffice(res.data.office_access);
        i18n.changeLanguage(res.data.language_pref || 'en');
      }).catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  const login = async (username, password) => {
    const res = await auth.login(username, password);
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('lang', res.data.user.language_pref || 'en');
    setUser(res.data.user);
    if (res.data.user.office_access !== 'both') setActiveOffice(res.data.user.office_access);
    i18n.changeLanguage(res.data.user.language_pref || 'en');
  };

  const logout = () => { localStorage.removeItem('token'); setUser(null); };

  const switchLanguage = async (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
    try { await usersApi.updateLanguage(lang); } catch (e) {}
    setUser(prev => ({ ...prev, language_pref: lang }));
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#EFF6FF' }}>
      <div style={{ textAlign:'center' }}>
        <div className="spinner" style={{ width:40, height:40 }} />
        <p style={{ color:'#1A3C6E', fontWeight:600, marginTop:12 }}>FINsathi IT Portal</p>
      </div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, activeOffice, setActiveOffice, switchLanguage }}>
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<ClientDatabase />} />
            <Route path="downloads" element={<Downloads />} />
            <Route path="users" element={user?.role === 'admin' ? <UserManagement /> : <Navigate to="/" />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
